// =============================================================================
// FlaggedContentPanel — the unified cross-surface moderation queue.
//
// The Stories table only ever surfaced reports on JOURNALS. Reader reports
// on Blueprints (posters), Moments (wall) and Journeys (reels) live in
// other tables (story_reports/poster_id, wall_reports, reel_reports) that
// the journal-centric page never read — so they collected silently with no
// operator ever seeing them.
//
// This panel closes that gap: it fetches the whole open-report queue across
// every surface (scope=all) and lets the operator dismiss any item's
// reports, routed through the same audited orchestrator the journal path
// uses. Self-contained (owns its own fetch + state) so it drops into the
// Stories page with a single mount and no entanglement with the table.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Flag, RefreshCw, Check, Loader2 } from 'lucide-react';
import {
  fetchAllTenantReports,
  dismissContentReports,
  type TenantFlaggedItem,
  type ReportTargetType,
} from '@/lib/api';
import { reasonMeta } from '@/lib/moderationCodes';

/** Per-surface display chrome — label + chip palette. */
const TYPE_META: Record<ReportTargetType, { label: string; cls: string }> = {
  journal:      { label: 'Story',     cls: 'bg-brand-50 text-brand-700 border-brand-200' },
  poster:       { label: 'Blueprint', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  wall_video:   { label: 'Moment',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  wall_cleanup: { label: 'Moment',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  wall_upload:  { label: 'Moment',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  reel:         { label: 'Journey',   cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

interface Props {
  tenantId: string;
}

export default function FlaggedContentPanel({ tenantId }: Props) {
  const [items,   setItems]   = useState<TenantFlaggedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error,   setError]   = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAllTenantReports(tenantId);
      setItems(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load flagged content');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const handleDismiss = async (item: TenantFlaggedItem) => {
    const key = `${item.target_type}:${item.target_id}`;
    setBusyKey(key);
    const r = await dismissContentReports({
      tenantId,
      targetType: item.target_type,
      targetId:   item.target_id,
    });
    setBusyKey(null);
    if (r.ok) {
      setItems(prev => prev.filter(i => `${i.target_type}:${i.target_id}` !== key));
    } else {
      setError(r.error || 'Dismiss failed');
    }
  };

  // Nothing flagged anywhere — render a quiet all-clear so the operator
  // knows the queue was actually checked (not just empty for lack of data).
  const isEmpty = !loading && items.length === 0;

  return (
    <div className="rounded-xl border border-surface-200 bg-surface-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 bg-surface-50">
        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-rose-600" />
          <h3 className="text-sm font-semibold text-surface-900">Flagged content</h3>
          <span className="text-xs text-surface-500">all surfaces</span>
          {items.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] px-1.5 h-5 rounded-full bg-rose-600 text-white text-[11px] font-bold">
              {items.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-surface-300 text-surface-900 bg-surface-0 hover:bg-surface-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <p className="px-4 py-2 text-xs text-rose-700 bg-rose-50 border-b border-rose-200">{error}</p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-4 h-4 animate-spin text-surface-500" />
        </div>
      ) : isEmpty ? (
        <div className="flex items-center gap-2 px-4 py-6 text-sm text-surface-500">
          <Check className="w-4 h-4 text-emerald-500" />
          No open reports on any surface.
        </div>
      ) : (
        <ul className="divide-y divide-surface-100">
          {items.map(item => {
            const key  = `${item.target_type}:${item.target_id}`;
            const meta = TYPE_META[item.target_type] ?? { label: item.target_type, cls: 'bg-surface-100 text-surface-700 border-surface-200' };
            const busy = busyKey === key;
            return (
              <li key={key} className="flex items-start gap-3 px-4 py-3">
                <span className={`shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${meta.cls}`}>
                  {meta.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-surface-900 truncate">
                      {item.title || item.target_id}
                    </p>
                    <span className="inline-flex items-center justify-center min-w-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold">
                      {item.open_count}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {Object.entries(item.reasons).map(([code, n]) => (
                      <span
                        key={code}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-100 text-surface-600"
                      >
                        {reasonMeta(code).label}
                        {n > 1 && <span className="font-bold">×{n}</span>}
                      </span>
                    ))}
                    <span className="text-[10px] text-surface-400">· {relativeTime(item.latest_at)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDismiss(item)}
                  disabled={busy}
                  className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-surface-300 text-surface-700 bg-surface-0 hover:bg-surface-50 disabled:opacity-50 transition-colors"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Dismiss
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
